import { Button, Icon, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { PageLayout } from '@hero/components/layouts';
import { EmptyTableContent } from '@hero/components/molecules/EmptyTableContent';
import { SettingsTabs } from '@hero/components/organisms';
import { PAYMENT_METHOD_STATUS } from '@hero/constants';
import { OrderStatusBadge, Table } from '@hero/molecules';
import { useGetPaymentMethods, useGetProviderUrl } from '@hero/services/organization-service/hooks';
import { addPaymentMethodLink } from '@hero/utils';

import * as S from './style';

export default function PaymentMethodsPage() {
  const { t } = useTranslation();

  const { data: providerUrl } = useGetProviderUrl();
  const { data: { paymentMethods } = {}, isLoading: isPaymentMethodsLoading } =
    useGetPaymentMethods();

  const handlePaymentMethodLink = () => {
    if (providerUrl) {
      window.open(addPaymentMethodLink(providerUrl?.loginLink, window.location.href), '_self');
    }
  };

  const [{ pageSize, pageIndex }, setPagination] = React.useState({
    total: paymentMethods?.length
  });

  const isHouseAccount = useMemo(() => {
    return (
      !!paymentMethods &&
      paymentMethods?.find(paymentMethod => paymentMethod.type === 'house-account')
    );
  }, [paymentMethods]);

  const columns = React.useMemo(
    () => [
      {
        id: 'paymentMethodType',
        header: t('paymentMethodType'),
        accessorKey: 'paymentMethodType',
        cell: () => (isHouseAccount ? t('houseAccount') : t('creditCard'))
      },
      ...(!isHouseAccount
        ? [
            {
              id: 'name',
              header: t('cardDescription'),
              accessorKey: 'name'
            }
          ]
        : []),
      {
        id: 'active',
        header: t('status'),
        accessorKey: 'active',
        cell: ({
          cell: {
            row: {
              original: { active }
            }
          }
        }) => {
          return (
            <OrderStatusBadge
              status={active ? PAYMENT_METHOD_STATUS.active : PAYMENT_METHOD_STATUS.inactive}
            />
          );
        }
      }
      // {
      //   id: 'actions',
      //   header: '',
      //   cell: ({ cell: { row } }) =>
      //     row.original.type !== 'house-account' && (
      //       <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
      //         <OptionsDropdown
      //           popperOptions={{
      //             placement: 'left',
      //             modifiers: [
      //               {
      //                 name: 'offset',
      //                 enabled: true,
      //                 options: {
      //                   offset: [50, 0]
      //                 }
      //               }
      //             ]
      //           }}
      //           options={[
      //             {
      //               label: t('edit'),
      //               onClick: () => {
      //                 window.open(
      //                   editPaymentMethodLink(
      //                     providerUrl?.loginLink,
      //                     window.location.href,
      //                     row.original.code
      //                   ),
      //                   'blank'
      //                 );
      //               }
      //             }
      //           ]}
      //         >
      //           <Icon name="3dot" px={18} />
      //         </OptionsDropdown>
      //       </div>
      //     )
      // }
    ],
    [isHouseAccount, isPaymentMethodsLoading]
  );

  return (
    <PageLayout
      header={{
        title: t('paymentMethods')
      }}
    >
      <SettingsTabs />

      <S.PaymentMethodWrapper>
        <S.InfoHeaderWrapper>
          <Typography variant="body2">
            <Trans
              i18nKey="total"
              values={{
                count: paymentMethods?.length
              }}
              components={{
                b: <b />
              }}
            />
          </Typography>
          {!!paymentMethods?.length && (
            <>
              <Typography variant="body2">
                {t('activeCount', { count: paymentMethods?.filter(item => item.active).length })}
              </Typography>
              <Typography variant="body2">
                {t('inactiveCount', { count: paymentMethods?.filter(item => !item.active).length })}
              </Typography>
            </>
          )}
          {!isPaymentMethodsLoading && !isHouseAccount && (
            <>
              <VerticalDivider $height={21} />
              <Button
                variant="link"
                startIcon={<Icon name="external-link" />}
                onClick={handlePaymentMethodLink}
              >
                {t('addNew')}
              </Button>
            </>
          )}
        </S.InfoHeaderWrapper>
        <Table
          columns={columns}
          data={paymentMethods || []}
          loading={isPaymentMethodsLoading}
          pageCount={1}
          enabled={{ filter: false, header: true, search: false }}
          setPagination={setPagination}
          pagination={{
            pageIndex,
            pageSize,
            total: paymentMethods?.length
          }}
          components={{
            EmptyTable: EmptyTable
          }}
        />
      </S.PaymentMethodWrapper>
    </PageLayout>
  );
}

function EmptyTable({ table }) {
  const { t } = useTranslation();
  const { data: providerUrl } = useGetProviderUrl();
  const searchTerm = table.getState().globalFilters?.searchTerm;

  const currentPath = window.location.href;

  const handlePaymentMethodLink = () => {
    if (providerUrl) {
      window.open(addPaymentMethodLink(providerUrl?.loginLink, currentPath), '_self');
    }
  };

  if (searchTerm) {
    return <EmptyTableContent />;
  }

  return (
    <S.EmptyTable>
      <Icon name="payment-method" size="xxl" color="var(--color-success-default)" />
      <S.EmptyTableText variant="body3SemiBold">{t('addFirstPM')}</S.EmptyTableText>
      <Button startIcon={<Icon name="add" />} onClick={handlePaymentMethodLink} size="sm">
        {t('addPaymentMethod')}
      </Button>
    </S.EmptyTable>
  );
}
